import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../components/PageHeader';
import TabHeader from '../components/TabHeader';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import type { SubcontractorUser } from '../features/planning/types';
import type { SettingsSubcontractorRow } from '../features/settings/types';
import SubcontractorRow from '../features/settings/components/subcontractors/SubcontractorRow';
import SubcontractorModal from '../features/settings/components/subcontractors/SubcontractorModal';
import UserModal from '../features/settings/components/subcontractors/UserModal';
import { useManageSubcontractors } from '../features/settings/hooks/useManageSubcontractors';
import { useNotification } from '../context/useNotification';
import { SUBCONTRACTORS_TAB_LABELS as LABELS } from '../utils/labels';
import { hasFullAccess, canManageBookings } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface SubcontractorsPageProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface SubcontractorModalState {
  mode: 'create' | 'edit';
  subcontractor?: SettingsSubcontractorRow;
}

interface UserModalState {
  mode: 'create' | 'edit';
  subcontractorId: string;
  user?: SubcontractorUser;
}

export default function SubcontractorsPage({ orgId, isSuperAdmin, role }: SubcontractorsPageProps) {
  // Adding/editing a subcontractor and fully managing its contact persons is
  // allowed for booking_only too — same access as the "+ nieuwe
  // subcontractor/contactpersoon" quick-create already has in the booking
  // form. Archiving/deleting the subcontractor itself stays full_access-only
  // (enforced server-side too, see the restrict_subcontractor_archive_delete
  // trigger).
  const canManage = canManageBookings(role, isSuperAdmin);
  const canArchiveOrDelete = hasFullAccess(role, isSuperAdmin);
  const {
    subcontractors,
    archivedSubcontractors,
    archivedLoaded,
    loadArchived,
    addSubcontractor,
    updateSubcontractor,
    archiveSubcontractor,
    unarchiveSubcontractor,
    deleteSubcontractor,
    addUser,
    updateUser,
    deleteUser,
    setDefaultUser,
  } = useManageSubcontractors(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [subcontractorModal, setSubcontractorModal] = useState<SubcontractorModalState | null>(null);
  const [userModal, setUserModal] = useState<UserModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<{ userId: string; subId: string } | null>(null);

  const handleToggle = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    setExpandedId(null);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteSubcontractor(id);
    if (result === 'ok') {
      notifySuccess(LABELS.deleteSuccess);
    } else if (result === 'has_bookings') {
      setDeleteError(LABELS.deleteErrorHasBookings);
      notifyError(LABELS.deleteErrorHasBookings);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;
    const { userId, subId } = pendingDeleteUser;
    setPendingDeleteUser(null);
    const ok = await deleteUser(userId, subId);
    if (ok) notifySuccess(LABELS.userDeleteSuccess); else notifyError(LABELS.mutationError);
  };

  const handleSubcontractorSave = async (name: string): Promise<boolean> => {
    const ok = subcontractorModal?.mode === 'create'
      ? await addSubcontractor(name)
      : subcontractorModal?.subcontractor
        ? await updateSubcontractor(subcontractorModal.subcontractor.id, name)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleUserSave = async (firstName: string, lastName: string): Promise<boolean> => {
    if (!userModal) return false;
    const ok = userModal.mode === 'create'
      ? await addUser(userModal.subcontractorId, firstName, lastName)
      : userModal.user
        ? await updateUser(userModal.user.id, firstName, lastName)
        : false;
    if (ok) notifySuccess(LABELS.userSaveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveSubcontractor(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveSubcontractor(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleSetDefaultUser = async (userId: string, subId: string) => {
    const ok = await setDefaultUser(userId, subId);
    if (ok) notifySuccess(LABELS.userDefaultSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? subcontractors : archivedSubcontractors;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={canManage ? LABELS.newButton : undefined}
            onAdd={canManage ? () => setSubcontractorModal({ mode: 'create' }) : undefined}
          />

          {deleteError && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
              <p className="text-sm text-red-700 flex-1">{deleteError}</p>
              <Tooltip title={LABELS.dismissError}>
                <IconButton size="small" onClick={() => setDeleteError(null)} aria-label={LABELS.dismissError}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
              <Tab label={LABELS.tabActive} value="active" />
              <Tab label={LABELS.tabArchived} value="archived" />
            </Tabs>

            {list.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
              </p>
            ) : (
              list.map((s, i) => (
                <SubcontractorRow
                  key={s.id}
                  subcontractor={s}
                  showDivider={i > 0}
                  expanded={expandedId === s.id}
                  onToggle={() => handleToggle(s.id)}
                  onEdit={canManage ? () => setSubcontractorModal({ mode: 'edit', subcontractor: s }) : undefined}
                  onArchive={canArchiveOrDelete && !s.archived ? () => handleArchive(s.id) : undefined}
                  onUnarchive={canArchiveOrDelete && s.archived ? () => handleUnarchive(s.id) : undefined}
                  onDelete={canArchiveOrDelete ? () => setPendingDeleteId(s.id) : undefined}
                  onAddUser={canManage ? () => setUserModal({ mode: 'create', subcontractorId: s.id }) : undefined}
                  onEditUser={canManage ? (user) => setUserModal({ mode: 'edit', subcontractorId: s.id, user }) : undefined}
                  onDeleteUser={canManage ? (uid) => setPendingDeleteUser({ userId: uid, subId: s.id }) : undefined}
                  onSetDefaultUser={canManage ? (uid) => handleSetDefaultUser(uid, s.id) : undefined}
                />
              ))
            )}
          </div>

          {subcontractorModal && (
            <SubcontractorModal
              subcontractor={subcontractorModal.subcontractor}
              onClose={() => setSubcontractorModal(null)}
              onSave={handleSubcontractorSave}
            />
          )}
          {userModal && (
            <UserModal
              user={userModal.user}
              onClose={() => setUserModal(null)}
              onSave={handleUserSave}
            />
          )}

          <ConfirmDeleteDialog
            open={!!pendingDeleteId}
            onClose={() => setPendingDeleteId(null)}
            onConfirm={handleConfirmDelete}
          />
          <ConfirmDeleteDialog
            open={!!pendingDeleteUser}
            onClose={() => setPendingDeleteUser(null)}
            onConfirm={handleConfirmDeleteUser}
          />
        </div>
      </main>
    </div>
  );
}
